import React, { useEffect, useMemo, useState } from "react";
import "./EditInvoice.scss";
import { MdArrowBack } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { adminRequest } from "../../../utils/requestMethods";

const semesterOptions = [
  "First Semester",
  "Second Semester",
  "Third Semester",
  "Fourth Semester",
  "Fifth Semester",
  "Sixth Semester",
  "Seventh Semester",
  "Eighth Semester",
];

const EditInvoice = () => {
  const { uuid } = useParams();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    semester: "",

    discount_amount: 0,

    discount_reason: "",

    items: [],
  });

  const fetchInvoice = async () => {
    try {
      const response = await adminRequest.get(`/invoice/view/${uuid}`);

      const invoice = response.data.data;

      setFormData({
        semester: invoice.semester,

        discount_amount: invoice.discount_amount,

        discount_reason: invoice.discount_reason || "",

        items:
          invoice.FeeInvoiceItems?.map((item) => ({
            title: item.title,

            amount: item.amount,

            description: item.description,
          })) || [],
      });
    } catch (error) {
      console.log(error);

      toast.error("Failed to fetch invoice");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,

      [e.target.name]: e.target.value,
    });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];

    updatedItems[index][field] = value;

    setFormData({
      ...formData,

      items: updatedItems,
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,

      items: [
        ...formData.items,

        {
          title: "",

          amount: "",

          description: "",
        },
      ],
    });
  };

  const removeItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);

    setFormData({
      ...formData,

      items: updatedItems,
    });
  };

  const totalAmount = useMemo(() => {
    return formData.items.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0,
    );
  }, [formData.items]);

  const payableAmount = totalAmount - Number(formData.discount_amount || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      await adminRequest.post(`/invoice/edit`, {
        uuid,

        ...formData,
      });

      toast.success("Invoice updated successfully");
      navigate(`/studentFees`);
    } catch (error) {
      console.log(error);

      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="editInvoiceContainer">Loading...</div>;
  }

  return (
    <div className="editInvoiceContainer">
      <div className="pageHeader">
        <div className="leftSection">
          <button className="backBtn" onClick={() => navigate(-1)}>
            <MdArrowBack />
          </button>

          <h2>Edit Invoice</h2>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="topFields">
          <div className="formGroup">
            <label>Semester</label>

            <select
              name="semester"
              value={formData.semester}
              onChange={handleChange}
            >
              {semesterOptions.map((semester) => (
                <option key={semester} value={semester}>
                  {semester}
                </option>
              ))}
            </select>
          </div>

          <div className="formGroup">
            <label>Discount Amount</label>

            <input
              type="number"
              name="discount_amount"
              value={formData.discount_amount}
              onChange={handleChange}
            />
          </div>

          <div className="formGroup">
            <label>Discount Reason</label>

            <input
              type="text"
              name="discount_reason"
              value={formData.discount_reason}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="invoiceItemsSection">
          <div className="sectionHeader">
            <h3>Invoice Items</h3>

            <button type="button" className="addBtn" onClick={addItem}>
              + Add Item
            </button>
          </div>

          <div className="invoiceTable">
            {formData.items.map((item, index) => (
              <div className="invoiceItemRow" key={index}>
                <input
                  type="text"
                  placeholder="Title"
                  value={item.title}
                  onChange={(e) =>
                    handleItemChange(index, "title", e.target.value)
                  }
                />

                <input
                  type="number"
                  placeholder="Amount"
                  value={item.amount}
                  onChange={(e) =>
                    handleItemChange(index, "amount", e.target.value)
                  }
                />

                <input
                  type="text"
                  placeholder="Description"
                  value={item.description}
                  onChange={(e) =>
                    handleItemChange(index, "description", e.target.value)
                  }
                />

                <button
                  type="button"
                  className="removeBtn"
                  onClick={() => removeItem(index)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="summarySection">
          <div className="summaryCard">
            <h4>Total Amount</h4>

            <p>Rs. {totalAmount}</p>
          </div>

          <div className="summaryCard">
            <h4>Payable Amount</h4>

            <p>Rs. {payableAmount}</p>
          </div>
        </div>

        <div className="submitSection">
          <button type="submit" disabled={submitting}>
            {submitting ? "Updating..." : "Update Invoice"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditInvoice;
